import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Check, ArrowRight, Building2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sponsorTiers = [
  {
    name: 'Bronze Partner',
    amount: '$1,000',
    color: 'bronze',
    benefits: [
      'Logo on website sponsors page',
      'Social media recognition',
      'Annual impact report',
      'Certificate of partnership',
    ],
  },
  {
    name: 'Silver Partner',
    amount: '$5,000',
    color: 'silver',
    benefits: [
      'All Bronze benefits',
      'Logo on event materials',
      'Quarterly updates',
      'Employee volunteer opportunities',
      'Recognition at events',
    ],
  },
  {
    name: 'Gold Partner',
    amount: '$10,000+',
    color: 'gold',
    featured: true,
    benefits: [
      'All Silver benefits',
      'Featured logo placement',
      'Custom athlete sponsorship',
      'VIP event access',
      'Press release announcement',
      'Named program opportunity',
    ],
  },
];

const Sponsors = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    sponsorship_tier: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [partners, setPartners] = useState([]);
  // Spam defences: invisible to people, and a timestamp so the server can reject
  // submissions that arrive faster than anyone could type.
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAt = useRef(Date.now());

  useEffect(() => {
    axios.get(`${API}/partners`)
      .then((res) => setPartners(res.data || []))
      .catch((err) => console.error('Error fetching partners:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await axios.post(`${API}/sponsors/inquiry`, {
        ...formData,
        website: honeypot,
        form_loaded_at: formLoadedAt.current,
      });
      setSubmitStatus('success');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        sponsorship_tier: '',
        message: '',
      });
    } catch (error) {
      console.error('Sponsor inquiry error:', error);
      setSubmitStatus('error');
      if (error?.response?.status === 429 && error.response.data?.detail) {
        setErrorMessage(error.response.data.detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="sponsors-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Building2 className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold">
              Corporate Partnerships
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Partner with Lane 4 For All Kids Foundation and make a meaningful impact on youth athletics in your community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Why Partner</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
              Your Partnership Makes a Difference
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Corporate partnerships are the backbone of our mission. Your support directly funds equipment, registration fees, training programs, and scholarships for young athletes who would otherwise be left on the sidelines.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Beyond financial support, corporate partnerships provide visibility, employee engagement opportunities, and the chance to be part of a growing movement for equity in youth sports.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="tiers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Partnership Levels</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Sponsorship Tiers
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {sponsorTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`sponsor-tier ${tier.color} p-8 ${tier.featured ? 'transform scale-105' : ''}`}
                data-testid={`tier-${tier.color}`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-1 text-xs font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <Award className={`w-12 h-12 mx-auto mb-4 ${
                    tier.color === 'gold' ? 'text-amber-600' :
                    tier.color === 'silver' ? 'text-slate-500' :
                    'text-orange-600'
                  }`} />
                  <h3 className="font-heading text-2xl font-bold text-slate-900">{tier.name}</h3>
                  <p className="font-heading text-4xl font-bold text-red-600 mt-2">{tier.amount}</p>
                </div>
                <ul className="space-y-3">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <a href="#sponsor-form" className="block mt-8">
                  <Button className={`w-full rounded-none uppercase tracking-widest font-bold py-4 ${
                    tier.featured ? 'bg-red-600 text-white hover:bg-slate-900' : 'bg-slate-900 text-white hover:bg-red-600'
                  }`}>
                    Become a Partner
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Sponsors Placeholder */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Partners</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
            Current Sponsors
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            We're grateful to our founding partners who believe in our mission.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {partners.length > 0 ? (
              partners.map((partner) => {
                const logo = (
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    title={partner.name}
                    className="max-h-20 max-w-full object-contain"
                  />
                );
                return (
                  <div
                    key={partner.id}
                    className="aspect-video bg-white flex items-center justify-center border border-slate-200 p-4"
                    data-testid={`sponsor-logo-${partner.id}`}
                  >
                    {partner.website ? (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full">
                        {logo}
                      </a>
                    ) : logo}
                  </div>
                );
              })
            ) : (
              [1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="aspect-video bg-slate-100 flex items-center justify-center border border-slate-200"
                  data-testid={`sponsor-placeholder-${i}`}
                >
                  <Building2 className="w-12 h-12 text-slate-300" />
                </div>
              ))
            )}
          </div>
          {partners.length === 0 && (
            <p className="mt-8 text-slate-500">Your logo could be here. Become a sponsor today!</p>
          )}
        </div>
      </section>

      {/* Sponsor Inquiry Form */}
      <section id="sponsor-form" className="py-20 md:py-32 bg-slate-50" data-testid="sponsor-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Get Started</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
              Sponsor Inquiry
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Interested in becoming a partner? Fill out the form below and our team will be in touch.
            </p>
          </div>

          {submitStatus === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white p-12 border border-slate-200 text-center"
              data-testid="sponsor-success"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900">Thank You!</h3>
              <p className="mt-4 text-slate-600">
                We've received your sponsorship inquiry. A member of our partnerships team will be in touch within 2 business days.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 border border-slate-200" data-testid="sponsor-inquiry-form">
              {/* Honeypot: bots fill every field they find; positioned offscreen
                  rather than display:none, which some bots deliberately skip. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px' }}
                data-testid="sponsor-honeypot"
              />
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Company Name *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="sponsor-company-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Contact Name *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="sponsor-contact-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="sponsor-email-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="sponsor-phone-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700">Interested Sponsorship Level</Label>
                <Select
                  value={formData.sponsorship_tier}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, sponsorship_tier: value }))}
                >
                  <SelectTrigger className="mt-1 border-slate-200" data-testid="sponsor-tier-select">
                    <SelectValue placeholder="Select a tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bronze">Bronze Partner - $1,000</SelectItem>
                    <SelectItem value="silver">Silver Partner - $5,000</SelectItem>
                    <SelectItem value="gold">Gold Partner - $10,000+</SelectItem>
                    <SelectItem value="custom">Custom Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700">Message</Label>
                <Textarea
                  rows={4}
                  placeholder="Tell us about your company and sponsorship goals..."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="mt-1 border-slate-200"
                  data-testid="sponsor-message-input"
                />
              </div>

              {submitStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="sponsor-error">
                  {errorMessage || 'Something went wrong. Please try again.'}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold py-4"
                data-testid="sponsor-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Inquiry
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Individual Giving
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Not a business? Individual donations of any size help young athletes succeed.
          </p>
          <div className="mt-8">
            <Link to="/donate" data-testid="sponsors-donate-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Make a Personal Donation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sponsors;
